import React, { useCallback } from "react";

import useLocales from "../../../../hooks/useLocales";
import { useDirtyFormNavigation } from "../../../../hooks/useDirtyFormNavigation";
// @polaris
import { Card, Box, BlockStack, Text, InlineStack, Divider, Button } from "@shopify/polaris";
import { EditIcon } from "@shopify/polaris-icons";

const MainMenu = ({ customerExperience, items = [], activeItem, onItemClick, onEditClick }) => {
  const { translate } = useLocales();
  const { checkDirtyForm } = useDirtyFormNavigation();

  const handleItemClick = useCallback(
    async (itemId) => {
      const canLeave = await checkDirtyForm();
      if (canLeave && onItemClick) {
        onItemClick(itemId);
      }
    },
    [onItemClick, checkDirtyForm]
  );

  const handleEditClick = useCallback(async () => {
    const canLeave = await checkDirtyForm();
    if (canLeave && onEditClick) {
      onEditClick();
    }
  }, [checkDirtyForm, onEditClick]);

  // Generate initials from title for avatar
  const getInitials = (title) => {
    if (!title) return "CE";
    return title
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 3);
  };

  return (
    <Card roundedAbove="xs">
      <Box>
        <BlockStack gap="300">
          {/* Customer Experience Header */}
          <Box paddingInline="200" paddingBlock="200">
            <InlineStack wrap={false} gap="400" align="space-between">
              {/* Left side: Avatar, Title, Description */}
              <InlineStack wrap={false} gap="400">
                {/* Avatar/Icon */}
                <Box
                  style={{
                    width: "30px",
                    height: "30px",
                    backgroundColor: customerExperience?.icon ? "transparent" : "#52F490",
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: "30px",
                    minHeight: "30px",
                    overflow: "hidden",
                  }}
                >
                  {customerExperience?.icon ? (
                    <img
                      src={customerExperience.icon}
                      alt={customerExperience?.title || translate("customerExperiences.card.imageAlt")}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <Text
                      variant="bodySm"
                      fontWeight="bold"
                      style={{
                        fontSize: "11px",
                        color: "#000",
                        lineHeight: 1,
                      }}
                    >
                      {getInitials(customerExperience?.title)}
                    </Text>
                  )}
                </Box>
                {/* Title and Description */}
                <BlockStack gap="100">
                  <Text variant="headingMd" fontWeight="semibold">
                    {customerExperience?.title || translate("customerExperiences.defaultTitle")}
                  </Text>
                  {customerExperience?.description && (
                    <Text variant="bodySm" tone="subdued">
                      {customerExperience.description}
                    </Text>
                  )}
                </BlockStack>
              </InlineStack>

              {/* Right side: Edit Button */}
              {onEditClick && (
                <Button
                  icon={EditIcon}
                  onClick={handleEditClick}
                  variant="tertiary"
                  size="micro"
                  accessibilityLabel={translate("customerExperiences.mainMenu.editNameDescription")}
                />
              )}
            </InlineStack>
          </Box>

          <Divider tone="subdued" />

          {/* Menu items */}
          {items.map((item, index) => {
            // Render category divider/header
            if (item.type === "divider") {
              return (
                <Box key={`divider-${index}`} paddingBlockStart="200">
                  <Box paddingInline="200" paddingBlockEnd="100">
                    <Text variant="bodySm" tone="subdued" fontWeight="semibold">
                      {item.label}
                    </Text>
                  </Box>
                </Box>
              );
            }

            // Render clickable menu item
            return (
              <Box
                key={item.id}
                paddingInline="200"
                paddingBlock="200"
                style={{
                  borderRadius: "10px",
                  padding: "5px",
                  cursor: "pointer",
                  backgroundColor: activeItem === item.id ? "rgba(243, 243, 243, 1)" : undefined,
                }}
                onClick={() => handleItemClick(item.id)}
              >
                <InlineStack gap="200" align="start">
                  {item.icon}
                  <Text
                    variant="bodyMd"
                    fontWeight={activeItem === item.id ? "semibold" : "regular"}
                    tone={activeItem === item.id ? undefined : "subdued"}
                  >
                    {item.label}
                  </Text>
                </InlineStack>
              </Box>
            );
          })}
        </BlockStack>
      </Box>
    </Card>
  );
};

export default MainMenu;
